import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { logger } from '@/lib/logger';

interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  description?: string;
  price_monthly: number;
  price_yearly: number;
  features: string[];
  service_limit: number;
  booking_limit: number;
}

interface ProviderSubscription {
  id: string;
  plan_id: string;
  status: string;
  billing_period: 'monthly' | 'yearly';
  current_period_start?: string;
  current_period_end?: string;
  trial_ends_at?: string;
  cancel_at_period_end: boolean;
  plan?: SubscriptionPlan;
}

export function useSubscription() {
  const { user } = useAuth();

  const { data: subscription, isLoading, refetch } = useQuery<ProviderSubscription | null>({
    queryKey: ['provider-subscription', user?.id],
    queryFn: async () => {
      if (!user) {
        logger.info('useSubscription: No user');
        return null;
      }
      logger.info('useSubscription: Fetching for provider_id:', user.id);
      const { data, error } = await supabase
        .from('provider_subscriptions')
        .select('*, plan:subscription_plans(*)')
        .eq('provider_id', user.id)
        .single();
      if (error) {
        if (error.code === 'PGRST116') {
          logger.info('useSubscription: No subscription found');
          return null;
        }
        logger.error('useSubscription: Error:', error);
        throw error;
      }
      return data as ProviderSubscription;
    },
    enabled: !!user,
  });

  // Calcular días restantes de trial
  const trialDaysRemaining = subscription?.trial_ends_at
    ? Math.max(
        0,
        Math.ceil(
          (new Date(subscription.trial_ends_at).getTime() - new Date().getTime()) /
            (1000 * 60 * 60 * 24)
        )
      )
    : 0;

  // Determinar si está en trial activo
  const isTrial =
    subscription?.status === 'trialing' &&
    subscription?.trial_ends_at != null &&
    new Date(subscription.trial_ends_at) > new Date();

  // Calcular precios
  const plan = subscription?.plan as SubscriptionPlan | undefined;
  const billingPeriod = subscription?.billing_period || 'monthly';
  const price = billingPeriod === 'yearly'
    ? plan?.price_yearly
    : plan?.price_monthly;
  const pricePerMonth = billingPeriod === 'yearly'
    ? plan?.price_yearly ? Math.round((plan.price_yearly / 12) * 100) / 100 : 0
    : plan?.price_monthly || 0;

  // Verificar si es plan premium (varios nombres posibles)
  // Solo es premium si el status permite acceso (active o trial válido)
  const hasActiveAccess = subscription?.status === 'active' || isTrial;
  const isPremiumPlan = hasActiveAccess && (
    plan?.slug === 'premium' ||
    plan?.slug === 'profesional' ||
    plan?.slug === 'pro' ||
    plan?.name?.toLowerCase().includes('premium') ||
    plan?.name?.toLowerCase().includes('profesional') ||
    (plan?.price_monthly && plan.price_monthly >= 29)
  );

  return {
    subscription,
    plan,
    status: subscription?.status || 'inactive',
    billingPeriod,
    isTrial: isTrial || false,
    trialEndsAt: subscription?.trial_ends_at,
    trialDaysRemaining,
    price: price || 0,
    pricePerMonth,
    canAcceptOnlinePayments: plan?.slug !== 'basico',
    isPremium: isPremiumPlan,
    isLoading,
    loading: isLoading,
    error: null,
    refetch,
    refreshSubscription: refetch,
  };
}